// Register the custom element only if not already defined
if (!customElements.get("product-gallery")) {
  // Generate unique IDs for each product-gallery instance
  let productGalleryIdCounter = 0;

  class ProductGallery extends HTMLElement {
    resizeTimeout;
    constructor() {
      super();
      // Don't query variants in constructor - wait for connectedCallback
      // when element is in the DOM and we can scope queries properly

      // Assign a unique ID to this instance if it doesn't have one
      if (!this.id) {
        this.id = `product-gallery-${++productGalleryIdCounter}`;
      }
    }

    getProductInfoContainer() {
      // Find the closest product-info element to properly scope queries
      return this.closest("product-info");
    }

    initializeVariants() {
      const productInfo = this.getProductInfoContainer();
      const context = productInfo || document;
      const variantsData = context.querySelector(
        "variant-selects .json_variants"
      );
      if (variantsData) {
        try {
          this.variants = JSON.parse(variantsData.innerHTML || "");
        } catch (e) {
          console.error(e);
        }
      }
      this.mobileLayout = this.dataset.mobileLayout;
      this.mobileShowArrows =
        this.dataset.mobileArrows == "true" ? true : false;
      this.mobileShowBullets =
        this.dataset.mobileBullets == "true" ? true : false;
      this.mobileShowThumbs =
        this.dataset.mobileThumbs == "true" ? true : false;

      this.desktopLayout = this.dataset.desktopLayout;
      this.desktopShowArrows =
        this.dataset.desktopArrows == "true" ? true : false;
      this.desktopShowBullets =
        this.dataset.desktopBullets == "true" ? true : false;
      this.desktopShowThumbs =
        this.dataset.desktopThumbs == "true" ? true : false;

      this.gallerySwiper = null;
      this.thumbsSwiper = null;
      this.addEventListeners = this.addEventListeners.bind(this);
      this.onResize = this.onResize.bind(this);
      this.handleUpdateThumbs = this.handleUpdateThumbs.bind(this);
    }

    handleUpdateThumbs(swiper) {
      let visibleSlides = swiper.slides
        .map((slide, idx) =>
          slide.classList.contains("swiper-slide-active") ||
          slide.classList.contains("swiper-slide-fully-visible")
            ? idx
            : -1
        )
        .filter((idx) => idx !== -1);
      if (visibleSlides.length > 0) {
        this.thumbsSwiper.slides.map((slide, index) => {
          if (visibleSlides.includes(index)) {
            this.thumbsSwiper.slides[index].classList.add("active");
          } else {
            this.thumbsSwiper.slides[index].classList.remove("active");
          }
        });
      }
    }
    initializeSwiper() {
      // We recreate the swipers
      if (this.thumbsSwiper && "destroy" in this.thumbsSwiper) {
        this.thumbsSwiper.destroy();
      }
      if (this.gallerySwiper && "destroy" in this.gallerySwiper) {
        this.gallerySwiper.destroy();
      }
      let createSwiper = false;
      if (
        (window.innerWidth <= 767 && this.mobileLayout == "carousel") ||
        (window.innerWidth >= 768 && this.desktopLayout == "carousel")
      ) {
        createSwiper = true;
      }
      // Only recreate them if in the current device of the user the carousel layout is enabled
      if (createSwiper) {
        const activeGallery = this.querySelector(
          "[data-variant-image].active .product-gallery"
        );
        const activeGalleryThumbs = this.querySelector(
          "[data-variant-image].active .product-gallery-thumbs"
        );
        if (activeGalleryThumbs) {
          this.thumbsSwiper = new Swiper(activeGalleryThumbs, {
            slidesPerView: "auto",
            spaceBetween: 10,
            centerInsufficientSlides: true,
            multipleActiveThumbs: true,
            navigation: {
              nextEl: activeGalleryThumbs.querySelector(".swiper-button-next"),
              prevEl: activeGalleryThumbs.querySelector(".swiper-button-prev"),
            },
            pagination: {
              el: activeGalleryThumbs.querySelector(".swiper-pagination"),
              clickable: true,
            },
          });
        }
        if (activeGallery) {
          const thumbSider = this.thumbsSwiper;
          this.gallerySwiper = new Swiper(activeGallery, {
            slidesPerView: "auto",
            roundLengths: true,
            spaceBetween: 10,
            allowTouchMove: true,
            watchSlidesProgress: true,
            on: {
              slideChange: (swiper) => {
                this.handleUpdateThumbs(swiper);
              },
              progress: (swiper, progress) => {
                if (progress === 1) {
                  this.handleUpdateThumbs(swiper);
                }
              },
            },
            navigation:
              (this.currentBreakpoint == "desktop" && this.desktopShowArrows) ||
              (this.currentBreakpoint == "mobile" && this.mobileShowArrows)
                ? {
                    nextEl: activeGallery.querySelector(".swiper-button-next"),
                    prevEl: activeGallery.querySelector(".swiper-button-prev"),
                  }
                : false,
            pagination:
              (this.currentBreakpoint == "desktop" &&
                this.desktopShowBullets) ||
              (this.currentBreakpoint == "mobile" && this.mobileShowBullets)
                ? {
                    el: activeGallery.querySelector(".swiper-pagination"),
                    clickable: true,
                  }
                : false,
            thumbs:
              (this.currentBreakpoint == "desktop" && this.desktopShowThumbs) ||
              (this.currentBreakpoint == "mobile" && this.mobileShowThumbs)
                ? {
                    swiper: this.thumbsSwiper || null,
                    multipleActiveThumbs: true,
                  }
                : false,
          });
          this.gallerySwiper.on("transitionEnd", () => {
            this.handleUpdateThumbs(this.gallerySwiper);
          });
        }
      }
    }

    async initializePhotoSwipe() {
      // Only initialize once
      if (this.lightbox) return;

      // Get asset URLs from data attributes
      const lightboxUrl = this.dataset.photoswipeLightboxUrl;
      const videoPluginUrl = this.dataset.photoswipeVideoPluginUrl;
      const photoswipeUrl = this.dataset.photoswipeUrl;

      const PhotoSwipeLightbox = await import(lightboxUrl);
      const PhotoSwipeVideoPlugin = await import(videoPluginUrl);

      const leftArrowSVGString = `<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
                          <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"></path>
                      </svg>`;
      const rightArrowSVGString = `<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
                          <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"></path>
                      </svg>`;
      const closeSVGString = `<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
                          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"></path>
                      </svg>`;

      // Use a scoped selector that targets all .product-gallery elements within this instance
      // This allows PhotoSwipe to work with all variant galleries, not just the first one
      const gallerySelector = `#${this.id} .product-gallery`;

      this.lightbox = new PhotoSwipeLightbox.default({
        arrowPrevSVG: leftArrowSVGString,
        arrowNextSVG: rightArrowSVGString,
        closeSVG: closeSVGString,
        counter: false,
        zoom: false,
        gallery: gallerySelector,
        children: "a",
        bgOpacity: 1,
        initialZoomLevel: "fit",
        secondaryZoomLevel: 2,
        padding: { top: 40, bottom: 40, left: 0, right: 0 },
        pswpModule: () => import(photoswipeUrl),
      });

      const videoPlugin = new PhotoSwipeVideoPlugin.default(this.lightbox, {
        // options
      });

      this.lightbox.init();
    }

    connectedCallback() {
      this.initializeVariants();
      this.currentBreakpoint = window.innerWidth <= 767 ? "mobile" : "desktop";
      this.initializeSwiper();
      this.initializePhotoSwipe();
      this.addEventListeners();
    }

    disconnectedCallback() {
      // Clean up PhotoSwipe when element is removed
      if (this.lightbox) {
        this.lightbox.destroy();
        this.lightbox = null;
      }
      // Clean up resize listener
      if (this.onResize) {
        window.removeEventListener("resize", this.onResize);
      }
      // Clean up variant change subscription
      if (this.onVariantChangeUnsubscriber) {
        this.onVariantChangeUnsubscriber();
      }
    }
    onResize() {
      // Clear previous timeout
      clearTimeout(this.resizeTimeout);

      // Debounce resize handler
      this.resizeTimeout = setTimeout(
        function () {
          const newBreakpoint = window.innerWidth <= 767 ? "mobile" : "desktop";

          // Only reinitialize if breakpoint changed or layout mode changed
          if (this.currentBreakpoint !== newBreakpoint) {
            this.currentBreakpoint = newBreakpoint;
            this.initializeSwiper();
          }
        }.bind(this),
        150
      ); // 150ms debounce delay
    }
    switchToVariant(matchingVariant) {
      if (matchingVariant) {
        let imageAlt = matchingVariant.featured_image?.alt;
        if (imageAlt) {
          imageAlt = imageAlt
            .split("|")[0]
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "-")
            .replace(/-+/g, "-")
            .replace(/^-|-$/g, "");
          const currentActive = this.querySelector(
            `[data-variant-image="${imageAlt}"]`
          );
          const sliders = this.querySelectorAll(`[data-variant-image]`);
          [...sliders]?.map((slider) => {
            if (slider === currentActive) {
              slider?.classList?.add("active");
            } else {
              slider?.classList?.remove("active");
            }
          });
          this.initializeSwiper();
        }
      }
    }
    addEventListeners() {
      this.currentBreakpoint = window.innerWidth <= 767 ? "mobile" : "desktop";
      window.addEventListener("resize", this.onResize);

      // Listen for variant changes
      this.handleVariantChange = (event) => {
        const productInfo = this.getProductInfoContainer();
        const context = productInfo || document;
        let options = {};
        event.data.selectedOptionValues?.map((optionId) => {
          const optionElement = context.querySelector(
            `variant-selects [data-option-value-id="${optionId}"]`
          );
          if (optionElement) {
            options[`option${optionElement.dataset.optionIndex * 1 + 1}`] =
              optionElement.value;
          }
        });
        // Find the variant that matches all selected options
        const matchingVariant = this.variants.find((variant) => {
          return Object.keys(options).every((optionKey) => {
            return variant[optionKey] === options[optionKey];
          });
        });

        if (matchingVariant) {
          this.switchToVariant(matchingVariant);
        }
      };

      this.onVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleVariantChange.bind(this)
      );
    }
  }
  customElements.define("product-gallery", ProductGallery);
}
